import React, { useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Table,
  Button,
  Space,
  Tag,
} from '@lockerpm/design';

import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined
} from "@ant-design/icons";

import itemsComponents from "../../../../../components/items";
import commonComponents from "../../../../../components/common";

import {
  convertDateTime,
  getEnvironment,
  isExpiringSoon
} from '../../../../../utils/common'

const TableData = (props) => {
  const { TextCopy } = itemsComponents;
  const { OnboardingTooltip } = commonComponents;

  const { t } = useTranslation();
  const isReadOnly = useSelector((state) => state.project.isReadOnly)
  const {
    loading = false,
    className = '',
    data = [],
    params = {},
    environments = [],
    selectedRowKeys = [],
    selectionChange = () => { },
    onUpdate = () => { },
    onDelete = () => { },
  } = props;

  const [showText, setShowText] = useState(false)

  const columns = useMemo(() => {
    return [
      {
        title: t('common.no'),
        dataIndex: 'stt',
        key: 'stt',
        align: 'center',
        width: 80,
      },
      {
        title: t('secret.key'),
        dataIndex: 'key',
        key: 'key',
        width: 300,
        align: 'left',
        render: (_, record) => {
          return (
            <TextCopy
              value={record.secret.key}
            />
          );
        }
      },
      {
        title: <div className="flex items-center justify-between">
          <p>{t('secret.value')}</p>
          <div className="pr-1 cursor-pointer" onClick={() => setShowText(!showText)}>
            {
              !showText ? <EyeOutlined /> : <EyeInvisibleOutlined />
            }
          </div>
        </div>,
        dataIndex: 'value',
        key: 'value',
        align: 'left',
        width: 300,
        render: (_, record) => <TextCopy
          show={showText}
          align={'between'}
          value={record.secret.value}
        />
      },
      {
        title: t('secret.environment'),
        dataIndex: 'environmentId',
        key: 'environmentId',
        align: 'left',
        width: 200,
        render: (_, record) => {
          const environment = getEnvironment(record.environmentId, environments)
          return environment ? <div>
            <OnboardingTooltip
              title={environment?.key ? t('onboarding.env_all_default') : ''}
              placement="top"
              content={
                <Tag
                  color={environment?.color}
                >
                  <TextCopy
                    value={environment?.name || ''}
                  />
                </Tag>
              }
            />
          </div> : <></>
        }
      },
      {
        title: t('secret.expired_date'),
        dataIndex: 'expired_date',
        key: 'expired_date',
        align: 'center',
        width: 150,
        render: (_, record) => {
          const expiredDate = record.expired_date || record.expiredDate;
          if (!expiredDate) return '-';
          const expiring = isExpiringSoon(expiredDate);
          return (
            <TextCopy
              color={expiring ? '#F54F64' : ''}
              className={expiring ? 'text-danger font-semibold' : ''}
              value={convertDateTime(expiredDate, 'DD-MM-YYYY')}
              align={'center'}
            />
          );
        }
      },
      {
        title: t('common.created_time'),
        dataIndex: 'creationDate',
        key: 'creationDate',
        align: 'center',
        width: 150,
        render: (_, record) => <TextCopy
          value={convertDateTime(record.creationDate)}
          align={'center'}
        />
      },
      {
        title: t('common.updated_time'),
        dataIndex: 'updatedDate',
        key: 'updatedDate',
        align: 'center',
        width: 150,
        render: (_, record) => <TextCopy
          value={convertDateTime(record.updatedDate)}
          align={'center'}
        />
      },
      {
        title: t('common.actions'),
        dataIndex: 'actions',
        key: 'actions',
        align: 'center',
        fixed: 'right',
        width: 100,
        hide: isReadOnly,
        render: (_, record) => (
          <Space size={[8, 8]}>
            <Button
              icon={<EditOutlined />}
              type={'link'}
              onClick={() => onUpdate(record)}
            ></Button>
            <Button
              icon={<DeleteOutlined />}
              type={'link'}
              danger
              onClick={() => onDelete(record)}
            ></Button>
          </Space>
        ),
      },
    ].filter((c) => !c.hide)
  }, [isReadOnly, environments, showText])
  return (
    <Table
      className={className}
      columns={columns}
      dataSource={data.map((d, index) => ({ ...d, stt: index + 1 + (params.page - 1) * params.size }))}
      loading={loading}
      pagination={false}
      rowKey={(record) => record?.id}
      size="small"
      scroll={{ x: 1024 }}
      rowSelection={isReadOnly ? false : {
        columnWidth: 25,
        selectedRowKeys: selectedRowKeys,
        onChange: selectionChange,
      }}
    />
  );
}

export default TableData;
